import type { StatusVariants } from "@/components/molecules";
import type { PaginatedResponse, PaginationParams } from "@/types/api";

import type { TOP_UPS_STATUS } from "../reports";

export enum DinersStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  SUSPENDED = "SUSPENDED",
  INCOMPLETE = "INCOMPLETE",
}

export const dinerStatusMapForUpdating: Record<string, DinersStatus> = {
  Approved: DinersStatus.APPROVED,
  Pending: DinersStatus.PENDING,
  Rejected: DinersStatus.REJECTED,
};

export const dinerStatusMap: Record<string, DinersStatus> = {
  ...dinerStatusMapForUpdating,
  Active: DinersStatus.ACTIVE,
  Inactive: DinersStatus.INACTIVE,
  Incomplete: DinersStatus.INCOMPLETE,
  Suspended: DinersStatus.SUSPENDED,
};

export const DINERS_STATUS_VARIANT_FOR_UPDATING: StatusVariants = {
  Approved: { badge: "success", dot: "#65A30D" },
  Pending: { badge: "info", dot: "#D97706" },
  Rejected: { badge: "warning", dot: "#DC2626" },
};

export const DINERS_STATUS_VARIANT_MAP: StatusVariants = {
  ...DINERS_STATUS_VARIANT_FOR_UPDATING,
  Active: { badge: "success", dot: "#65A30D" },
  Inactive: { badge: "default", dot: "#111827" },
  Incomplete: { badge: "outline", dot: "#D2D5D9" },
  Suspended: { badge: "info", dot: "#D97706" },
};

export enum DineOutTimes {
  OneOrTwo = "OneOrTwo",
  ThreeOrFive = "ThreeOrFive",
  MoreThanSix = "MoreThanSix",
}

export const DineOutTimesLabel = {
  [DineOutTimes.OneOrTwo]: "1-2 times",
  [DineOutTimes.ThreeOrFive]: "3-5 times",
  [DineOutTimes.MoreThanSix]: "> 6 times",
};

export const DineOutTimesOptions = Object.values(DineOutTimes).map(value => ({
  name: DineOutTimesLabel[value],
  value: value,
  id: value,
}));

export enum FavoriteRestaurantType {
  "FineDine" = "FineDine",
  "UpperCasual" = "UpperCasual",
  "CasualDine" = "CasualDine",
}

export const FavoriteRestaurantLabels: Record<FavoriteRestaurantType, string> =
  {
    [FavoriteRestaurantType.FineDine]: "Fine Dine",
    [FavoriteRestaurantType.UpperCasual]: "Upper Casual",
    [FavoriteRestaurantType.CasualDine]: "Casual Dine",
  };

export enum MobileOs {
  "iOS" = "iOS",
  "Android" = "Android",
}

export type AdminDinerType = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  status: DinersStatus;
  ordersCount: number;
  totalSpent: number;
  createdAt: string;
  mobileOs: MobileOs;
  lastVisit: string;
  avg: string;
  firstVisit?: string;
  tier?: string | null;
  tierName?: string | null;
};

export type AdminDinerResponse = PaginatedResponse<AdminDinerType>;

export enum ReferralInsightMemberStatus {
  COMPLETED = "COMPLETED",
  PENDING = "PENDING",
}

export enum ReferralInsightMemberSortField {
  NAME = "NAME",
  JOINED_AT = "JOINED_AT",
  STATUS = "STATUS",
  REWARDS_EARNED = "REWARDS_EARNED",
}

export interface ReferralInsightsSummary {
  earnings: {
    currency: string;
    lifetime: number;
    expired: number;
  };
  invites: {
    total: number;
    completed: number;
    pending: number;
  };
}

export interface ReferralInsightMember {
  referralInviteId: string;
  referralRuleId: string | null;
  diner: {
    id: string;
    fullName: string | null;
  };
  joinedAt: string;
  status: ReferralInsightMemberStatus;
  rewardsEarned: number;
}

export interface ReferralInsightMembersParams extends PaginationParams {
  currency: string;
  status?: ReferralInsightMemberStatus;
  joinedAtFrom?: string;
  joinedAtTo?: string;
  sortBy?: ReferralInsightMemberSortField;
  sortDirection?: "asc" | "desc";
}

export interface ReferralInsightMembersResponse
  extends PaginatedResponse<ReferralInsightMember> {
  currency: string;
}

export interface DinerExtraInformation {
  socialLinkHandle: string;
  favoriteRestaurantType: string;
  dineOutTimes: DineOutTimes;
  mobileOs: string;
  deviceType: string;
  tier?: string | null;
  referralRules?: string[] | null;
  favoriteRestaurantDescription?: Record<string, string> | null;
}

export interface ITopUpRequest {
  amount: number;
  reason: string;
  /** Currency of the top-up. The backend uses SAR when it is left out. */
  currency?: string;
}

export interface TopUpInfo {
  id: string;
  /** Currency both amounts are in. */
  currency?: string;
  requestedAmount: number;
  approvedAmount: number;
  reason: string;
  status: TOP_UPS_STATUS;
  createdAt: string;
  requestedByName?: string | null;
  processedByName?: string | null;
  processedAt?: string | null;
  rejectionReason?: string | null;
}

export type TopUpInfoResponse = PaginatedResponse<TopUpInfo>;

export interface IDinerInfo {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string | null;
  status: DinersStatus;
  city: string;
  createdAt: string;
  approvedAt: string;
  languagePreference: string;
  birthDate: string | null;
  lastVisit: string;
  totalSpent: number;
  ordersCount: number;
  totalVisits: number;
  totalTips: number;
  totalBalance: number;
  extraInformation?: DinerExtraInformation | null;
  tierId?: string | null;
  referralRuleIds?: string[] | null;
  processedByAdmin?: string | null;
  followersCount: string;
  qualificationScore: string;
  totalCashbackEarned: number;
  totalCashbackSpent: number;
  totalCashbackExpired?: number | null;
  totalCashbackAddedBySpice?: number;
  totalTopUpsAmountToDate?: number;
  referralSourceUser?: string | null;
  referralSourceRule?: string | null;
}

export interface DinerPayload {
  ids: string[];
  status: DinersStatus;
}

export type DinerUpdateStatusPayload = DinerPayload;

export type UpdateDinersStatusResponse = {
  requestedToUpdate: number;
  updated: number;
};

export type AdminDinerSubType = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  totalOrders: number;
  totalSpend: number;
  createdAt: string;
  lastVisit: string;
  avgGuestsPerVisit: string;
};
